import React, { RefObject, useEffect, useRef, useState } from 'react'

import { ADD, useNotificationContext } from 'shared/components/Notification'
import { Button, FileItem, LoadedFile } from 'shared/components'

import classNames from 'classnames'
import classes from './AddDocument.module.scss'

type Props = {
  btnGray?: boolean
  maxFilesLength?: number | null
  maxFileSize?: number | null
  onChange?: (files: LoadedFile[]) => void
  onFileError: (err: string) => void
  setBtnDisabled?: (v: boolean) => void
  isShowed?: boolean
  disabled?: boolean
  allowFormat?: string[] // in uppercase
}

type AddedFile = {
  file: File
  loaded: boolean
  tusName?: string
}

export const AddDocument = ({
  btnGray,
  onChange = () => {},
  maxFilesLength = null,
  maxFileSize = null,
  onFileError,
  setBtnDisabled,
  isShowed,
  disabled,
  allowFormat,
}: Props): JSX.Element => {
  const fileRef: RefObject<HTMLInputElement> | null = useRef(null)
  const { notificationsDispatch } = useNotificationContext()
  const [addedFiles, setAddedFiles] = useState<AddedFile[]>([])
  const [addedFilesSize, setAddedFilesSize] = useState(0)

  useEffect(() => {
    const isAllLoaded = addedFiles.every(f => f.loaded)

    if (isAllLoaded && setBtnDisabled) {
      setBtnDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedFiles])

  useEffect(() => {
    isShowed && clearFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowed])

  const clearFiles = () => {
    if (fileRef?.current?.files) {
      fileRef.current.value = ''
    }

    setAddedFiles([])
    emitChange([])
  }

  const setNotification = (isContain?: boolean) => {
    const error = 'Во время загрузки возникла ошибка.'
    const lengthError = '\nЗагрузите повторно не более 5 файлов в одной заявке.'
    const sizeError = '\nОбщий размер файлов не должен превышать 5 MB.'
    const typeError = allowFormat?.length
      ? `\nФормат: ${allowFormat.join(', ')}.`
      : ''
    const description = isContain
      ? 'Файл(ы) уже добавлен(ы).'
      : `${error}${lengthError}${sizeError}${typeError}`

    notificationsDispatch({
      type: ADD,
      payload: {
        type: 'error',
        description,
      },
    })
  }

  const checkFiles = (uploadFiles: File[], uploadLength: number): boolean => {
    const isMaxCount = isLimitMaxLength(uploadLength)
    let commonSize = addedFilesSize

    if (isMaxCount) {
      return false
    }

    for (let i = 0; i < uploadFiles.length; i++) {
      const fName = uploadFiles[i].name
      const fExt = fName.substr(fName.lastIndexOf('.') + 1)
      const fSize = uploadFiles[i].size

      // allowFormat in uppercase
      if (allowFormat && !allowFormat.includes(fExt.toUpperCase())) {
        return false
      }
      if (maxFileSize !== null && commonSize + fSize > maxFileSize) {
        return false
      }

      commonSize += fSize
    }

    setAddedFilesSize(commonSize)

    return true
  }

  const isLimitMaxLength = (uploadLength: number) => {
    if (maxFilesLength === null) {
      return false
    }

    return addedFiles.length + uploadLength > maxFilesLength
  }

  const handleFiles = () => {
    const uploadFiles = Array.from(fileRef?.current?.files || [])
    const uploadFilesLength = uploadFiles.length

    if (uploadFilesLength) {
      const isContain = uploadFiles.some(f =>
        addedFiles.find(
          adF =>
            adF.file.name === f.name &&
            adF.file.size === f.size &&
            adF.file.type === f.type
        )
      )

      if (isContain) {
        setNotification(true)
        return
      }

      if (!checkFiles(uploadFiles, uploadFilesLength)) {
        setNotification()
        if (fileRef?.current?.files) {
          fileRef.current.value = ''
        }
        return
      }

      const temp: AddedFile[] = []
      for (let i = 0; i < uploadFilesLength; i++) {
        temp.push({
          file: uploadFiles[i],
          loaded: false,
        })
      }
      setBtnDisabled && setBtnDisabled(true)
      setAddedFiles([...addedFiles, ...temp])
    }
  }

  const handleDelete = (idx: number) => {
    if (fileRef?.current?.files) {
      fileRef.current.value = ''
    }

    const tempAdded = [...addedFiles]
    const deletedFile = tempAdded.splice(idx, 1)

    setAddedFilesSize(addedFilesSize - deletedFile[0].file.size)
    setAddedFiles(tempAdded)
    emitChange(tempAdded)
  }

  const emitChange = (files: AddedFile[]) => {
    const loadedFiles: LoadedFile[] = []

    for (let i = 0; i < files.length; i++) {
      if (files[i].loaded) {
        loadedFiles.push({
          fileName: files[i].file.name,
          fileSize: files[i].file.size,
          fileId: files[i]?.tusName || '',
        })
      }
    }

    onChange(loadedFiles)
  }

  const handleFileLoad = (idx: number, tusName: string) => {
    const f = addedFiles.find((f, i) => i === idx)

    if (f) {
      const temp = [...addedFiles]
      temp[idx].loaded = true
      temp[idx].tusName = tusName
      setAddedFiles(temp)
      emitChange(temp)
    }
  }

  const renderFiles = () => {
    const listClasses = classNames('mb-2', [classes.list], {
      [classes.disabled]: disabled,
    })

    return (
      <>
        {addedFiles.length > 0 && (
          <ul className={listClasses}>
            {addedFiles.map((file, i) => (
              <FileItem
                idx={i}
                key={`${file.file.name}_${file.file.size}`}
                file={file.file}
                setFileError={err => onFileError(err)}
                onLoadSuccess={(idx, tusName) => handleFileLoad(idx, tusName)}
                onLoadError={() => setBtnDisabled && setBtnDisabled(false)}
                onDelete={i => handleDelete(i)}
              />
            ))}
          </ul>
        )}

        <input
          ref={fileRef}
          type="file"
          multiple
          onChange={handleFiles}
          hidden
        />

        {(maxFilesLength === null || addedFiles.length < maxFilesLength) && (
          <Button
            size="small"
            btnType={btnGray ? 'outline_light' : 'outline_dark'}
            iconLeft="icon-plus"
            className={classes.btn}
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={disabled}
          >
            Добавить документ
          </Button>
        )}
      </>
    )
  }

  return renderFiles()
}
